import * as React from "react";
import {
  Accordion, AccordionSummary, AccordionDetails,
  Box, Button, Autocomplete, TextField, Grid,
  IconButton, Typography, Divider, MenuItem, Stack
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useStyles } from "../../../Theme/Style";
import { useState, useEffect } from "react";
import { Form, Formik, FieldArray } from "formik";
import { Delete, PictureAsPdf, AddCircleOutline } from "@mui/icons-material";
import * as Yup from "yup";
import { LoadingButton } from "@mui/lab";
import { useNotification } from "../../../Contexts/GlobalNotificationContext";
import {
  useSubmitEmbroideryMutation,
  useUploadEmbroideryImageMutation,
  useGetEmbroideryQuery,
} from "../../../services/api/production-order";
import {
  useGetEmbroideryLibraryListQuery
} from "../../../services/api/embroidery-library";
// ⬇️ NEW: organizations list
import { useGetOrganizationsAllListQuery } from "../../../services/api/embroidery-library";

const validationSchema = Yup.object({
  design_name: Yup.string().required("Design Name is required"),
  ribbon: Yup.string().nullable(),
  logo: Yup.string().nullable(),
  production_option: Yup.string().oneOf(["embroidery", "heat_transfer"]).required("Production option is required"),
  width: Yup.number().required("Width is required"),
  height: Yup.number().required("Height is required"),
  embroideryDiskName: Yup.string().required("Embroidery Disk Number is required"),
  remark: Yup.string().nullable(),

  // Arrays
  colorways: Yup.array().of(
    Yup.object({
      name: Yup.string().nullable(),
      image: Yup.string().nullable(), // server path returned by upload
    })
  ).min(1, "Add at least one colourway"),

  // NEW: logo_images JSON array (like colorways)
  logo_images: Yup.array().of(
    Yup.object({
      name: Yup.string().nullable(),
      image: Yup.string().nullable(),
    })
  ).min(1, "Add at least one logo image"),
});

const EmbroideryComponent = ({ prodId }) => {
  const classes = useStyles();
  const { addNotification } = useNotification();

  // Organizations & library
  const { data: organizations = [] } = useGetOrganizationsAllListQuery();
  const [selectedOrgId, setSelectedOrgId] = useState(null);
  const { data: libraries = [] } = useGetEmbroideryLibraryListQuery(selectedOrgId);
  const [selectedLibrary, setSelectedLibrary] = useState(null);

  // Mutations/queries
  const [uploadEmbroideryImages] = useUploadEmbroideryImageMutation();
  const [submitEmbroidery] = useSubmitEmbroideryMutation();
  const { data: embroideryData = {} } = useGetEmbroideryQuery({ id: prodId });

  const [initialValues, setInitialValues] = useState({
    design_name: "",
    ribbon: "",
    logo: null,
    production_option: "embroidery",
    width: "",
    height: "",
    embroideryDiskName: "",
    remark: "",
    colorways: [{ name: "", image: null }],
    // NEW
    logo_images: [{ name: "", image: null }],
  });

  // Previews for dynamic arrays (index-aligned)
  const [previewImages, setPreviewImages] = useState({
    colorwayPreviews: [],   // string[] (local object URLs)
    logoImagesPreviews: [], // string[] (local object URLs)
  });

  const pick = (obj, keys, def = null) => {
    for (const k of keys) if (obj && obj[k] != null) return obj[k];
    return def;
  };

  // Preselect org if present in library/record
  useEffect(() => {
    const src = selectedLibrary || embroideryData || {};
    if (!selectedOrgId && src.organization_id) {
      setSelectedOrgId(src.organization_id);
    }
  }, [selectedLibrary, embroideryData]); // eslint-disable-line react-hooks/exhaustive-deps

  // Initialize form state from library or loaded record
  useEffect(() => {
    const src = selectedLibrary || embroideryData || {};

    const colorwaysFromSrc = Array.isArray(src.colorways) && src.colorways.length
      ? src.colorways.map(cw => ({ name: cw?.name || "", image: cw?.image || null }))
      : [{ name: "", image: null }];

    // Build logo_images from new field or migrate legacy adult/junior
    let logoImagesFromSrc = [{ name: "", image: null }];
    if (Array.isArray(src.logo_images) && src.logo_images.length) {
      logoImagesFromSrc = src.logo_images.map(li => ({ name: li?.name || "", image: li?.image || null }));
    } else {
      const migrated = [];
      if (src.logo_image_adult) migrated.push({ name: "Adult", image: src.logo_image_adult });
      if (src.logo_image_junior) migrated.push({ name: "Junior", image: src.logo_image_junior });
      if (migrated.length) logoImagesFromSrc = migrated;
    }

    setInitialValues({
      design_name: pick(src, ["design_name"], ""),
      ribbon: pick(src, ["ribbon"], ""),
      logo: pick(src, ["logo"], null),
      production_option: pick(src, ["production_option"], "embroidery"),
      width: pick(src, ["width"], ""),
      height: pick(src, ["height"], ""),
      embroideryDiskName: pick(src, ["disk_name", "embroideryDiskName"], ""),
      remark: pick(src, ["remark"], ""),
      colorways: colorwaysFromSrc,
      logo_images: logoImagesFromSrc,
    });

    setPreviewImages({
      colorwayPreviews: (src.colorways || []).map(cw => cw?.image_url || null),
      logoImagesPreviews: (src.logo_images || []).map(li => li?.image_url || null),
    });
  }, [selectedLibrary, embroideryData]);

  const handleFileUpload = async (file, isPdf = false) => {
    const formData = new FormData();
    formData.append(isPdf ? "file" : "image", file);
    return uploadEmbroideryImages({ data: formData }).unwrap(); // { path, url }
  };

  const handleFileChange = async (e, key, setFieldValue, isPdf = false) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const res = await handleFileUpload(file, isPdf);
      setFieldValue(key, res.path);
      if (!isPdf) {
        setPreviewImages((prev) => ({ ...prev, [key]: URL.createObjectURL(file) }));
      }
    } catch {
      addNotification({ message: "Upload error", type: "error" });
    }
  };

  // Upload specifically for an image inside a dynamic array
  const handleIndexedImageChange = async (e, index, setFieldValue, arrayKey, previewKey) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const res = await handleFileUpload(file, false);
      setFieldValue(`${arrayKey}[${index}].image`, res.path);
      setPreviewImages((prev) => {
        const next = [...(prev[previewKey] || [])];
        next[index] = URL.createObjectURL(file);
        return { ...prev, [previewKey]: next };
      });
    } catch {
      addNotification({ message: "Upload error", type: "error" });
    }
  };

  const onSubmitData = async (values, { setSubmitting }) => {
    try {
      const data = {
        design_name: values.design_name,
        ribbon: values.production_option === "embroidery" ? values.ribbon : null,
        logo: values.logo,
        production_option: values.production_option,
        width: values.width,
        height: values.height,
        disk_name: values.embroideryDiskName,
        production_order_id: prodId,
        remark: values.remark,
        colorways: (values.colorways || []).map(cw => ({
          name: cw?.name || "",
          image: cw?.image || null,
        })),
        // NEW: send logo_images
        logo_images: (values.logo_images || []).map(li => ({
          name: li?.name || "",
          image: li?.image || null,
        })),
      };

      await submitEmbroidery({ data }).unwrap();
      addNotification({ message: "Submitted", type: "success" });
    } catch (e) {
      addNotification({ message: "Error", type: "error" });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Accordion className={classes.accordion}>
      <AccordionSummary
        expandIcon={<ExpandMoreIcon sx={{ color: "#fff" }} />}
        className={classes.accordionSummary}
      >
        <Grid container className={classes.accordionSummaryTextContainer}>
          <Grid item xs={6}>Embroidery</Grid>
          <Grid item xs={6}>刺绣</Grid>
        </Grid>
      </AccordionSummary>

      <AccordionDetails className={classes.accordionDetails}>
        {/* Organization selector */}
        <Autocomplete
          options={organizations}
          getOptionLabel={(o) => o.name_with_id}
          value={organizations.find((o) => o.id === selectedOrgId) || null}
          onChange={(_, v) => {
            setSelectedOrgId(v?.id || null);
            setSelectedLibrary(null);
          }}
          renderInput={(params) => (
            <TextField {...params} label="Organization" />
          )}
          sx={{ mb: 2 }}
        />

        {/* Library selector (filtered by org) */}
        <Autocomplete
          options={libraries}
          getOptionLabel={(lib) => lib.design_name_with_disk || ""}
          value={selectedLibrary}
          onChange={(_, lib) => setSelectedLibrary(lib)}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Select Library"
              placeholder={
                selectedOrgId ? "Choose from organization’s libraries" : "Select organization first"
              }
            />
          )}
          disabled={!selectedOrgId}
          sx={{ mb: 2 }}
        />

        <Formik
          initialValues={initialValues}
          enableReinitialize
          validationSchema={validationSchema}
          onSubmit={onSubmitData}
        >
          {({ values, errors, touched, setFieldValue, isSubmitting }) => (
            <Form>
              {/* Production Option */}
              <Box sx={{ mb: 2 }}>
                <TextField
                  select
                  fullWidth
                  label="Production Option"
                  value={values.production_option}
                  onChange={(e) => setFieldValue("production_option", e.target.value)}
                  error={touched.production_option && Boolean(errors.production_option)}
                  helperText={touched.production_option && errors.production_option}
                >
                  <MenuItem value="embroidery">Embroidery</MenuItem>
                  <MenuItem value="heat_transfer">Heat Transfer</MenuItem>
                </TextField>
              </Box>

              <Grid container spacing={2}>
                {/* Core fields */}
                <Grid item xs={6}>
                  <TextField
                    fullWidth label="Design Name"
                    value={values.design_name}
                    onChange={(e) => setFieldValue("design_name", e.target.value)}
                    error={touched.design_name && Boolean(errors.design_name)}
                    helperText={touched.design_name && errors.design_name}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth label="Embroidery Disk Number"
                    value={values.embroideryDiskName}
                    onChange={(e) => setFieldValue("embroideryDiskName", e.target.value)}
                    error={touched.embroideryDiskName && Boolean(errors.embroideryDiskName)}
                    helperText={touched.embroideryDiskName && errors.embroideryDiskName}
                  />
                </Grid>

                <Grid item xs={4}>
                  <TextField
                    fullWidth label="Width"
                    value={values.width}
                    onChange={(e) => setFieldValue("width", e.target.value)}
                    error={touched.width && Boolean(errors.width)}
                    helperText={touched.width && errors.width}
                  />
                </Grid>
                <Grid item xs={4}>
                  <TextField
                    fullWidth label="Height"
                    value={values.height}
                    onChange={(e) => setFieldValue("height", e.target.value)}
                    error={touched.height && Boolean(errors.height)}
                    helperText={touched.height && errors.height}
                  />
                </Grid>

                {/* Ribbon — hide when heat_transfer */}
                {values.production_option === "embroidery" && (
                  <Grid item xs={4}>
                    <TextField
                      fullWidth label="Ribbon"
                      value={values.ribbon}
                      onChange={(e) => setFieldValue("ribbon", e.target.value)}
                    />
                  </Grid>
                )}

                <Divider flexItem sx={{ my: 1, width: "100%" }} />

                {/* PDF upload */}
                <Grid item xs={12}>
                  <input
                    accept="application/pdf"
                    style={{ display: "none" }}
                    id="logo-pdf"
                    type="file"
                    onChange={(e) => handleFileChange(e, "logo", setFieldValue, true)}
                  />
                  <label htmlFor="logo-pdf">
                    <Button startIcon={<PictureAsPdf />} component="span">
                      {values.logo ? (typeof values.logo === "string" ? values.logo.split("/").pop() : "PDF Uploaded") : "Upload PDF Logo"}
                    </Button>
                  </label>
                </Grid>

                <Divider flexItem sx={{ my: 1, width: "100%" }} />

                {/* NEW: Dynamic Logo Images */}
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ width: "100%", px: 2, mt: 1 }}
                >
                  <Typography variant="h6">Logo Images</Typography>
                  <Button
                    variant="outlined"
                    startIcon={<AddCircleOutline />}
                    onClick={() =>
                      setFieldValue("logo_images", [...(values.logo_images || []), { name: "", image: null }])
                    }
                  >
                    Add logo image
                  </Button>
                </Stack>

                <Grid item xs={12}>
                  <FieldArray
                    name="logo_images"
                    render={(arrayHelpers) => (
                      <>
                        {(values.logo_images || []).map((li, i) => (
                          <Box key={`logoimg-${i}`} sx={{ mt: 1 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                              Logo Image #{i + 1}
                            </Typography>

                            <Grid container spacing={2} alignItems="flex-start">
                              <Grid item xs={6}>
                                <TextField
                                  fullWidth
                                  label="Label / Name (e.g., Adult, Junior)"
                                  value={li.name || ""}
                                  onChange={(e) => setFieldValue(`logo_images[${i}].name`, e.target.value)}
                                  error={Boolean(touched.logo_images?.[i]?.name && errors.logo_images?.[i]?.name)}
                                  helperText={touched.logo_images?.[i]?.name && errors.logo_images?.[i]?.name}
                                />
                              </Grid>

                              <Grid item xs={6}>
                                <input
                                  accept="image/*"
                                  style={{ display: "none" }}
                                  id={`logo_images_${i}_image`}
                                  type="file"
                                  onChange={(e) =>
                                    handleIndexedImageChange(e, i, setFieldValue, "logo_images", "logoImagesPreviews")
                                  }
                                />
                                <label htmlFor={`logo_images_${i}_image`}>
                                  <Button component="span">Upload Image</Button>
                                </label>

                                {(previewImages.logoImagesPreviews?.[i] || li.image) && (
                                  <Box mt={1}>
                                    <img
                                      src={previewImages.logoImagesPreviews?.[i] || li.image}
                                      style={{ width: "100%", maxHeight: 180 }}
                                      alt={`Logo Image #${i + 1}`}
                                    />
                                    <Stack direction="row" spacing={1} mt={0.5}>
                                      <IconButton
                                        size="small"
                                        onClick={() => {
                                          setFieldValue(`logo_images[${i}].image`, null);
                                          setPreviewImages((p) => {
                                            const next = [...(p.logoImagesPreviews || [])];
                                            next[i] = null;
                                            return { ...p, logoImagesPreviews: next };
                                          });
                                        }}
                                        title="Remove image"
                                      >
                                        <Delete />
                                      </IconButton>
                                      <Button
                                        size="small"
                                        color="error"
                                        onClick={() => {
                                          arrayHelpers.remove(i);
                                          setPreviewImages((p) => {
                                            const next = [...(p.logoImagesPreviews || [])];
                                            next.splice(i, 1);
                                            return { ...p, logoImagesPreviews: next };
                                          });
                                        }}
                                      >
                                        Remove row
                                      </Button>
                                    </Stack>
                                  </Box>
                                )}
                              </Grid>
                            </Grid>

                            <Divider sx={{ my: 2 }} />
                          </Box>
                        ))}
                      </>
                    )}
                  />
                </Grid>

                <Divider flexItem sx={{ my: 1, width: "100%" }} />

                {/* NEW: Dynamic Colourways */}
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ width: "100%", px: 2, mt: 1 }}
                >
                  <Typography variant="h6">Colourways</Typography>
                  <Button
                    variant="outlined"
                    startIcon={<AddCircleOutline />}
                    onClick={() =>
                      setFieldValue("colorways", [...(values.colorways || []), { name: "", image: null }])
                    }
                  >
                    Add colourway
                  </Button>
                </Stack>

                <Grid item xs={12}>
                  <FieldArray
                    name="colorways"
                    render={(arrayHelpers) => (
                      <>
                        {(values.colorways || []).map((cw, i) => (
                          <Box key={i} sx={{ mt: 1 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                              Colourway #{i + 1}
                            </Typography>

                            <Grid container spacing={2} alignItems="flex-start">
                              <Grid item xs={6}>
                                <TextField
                                  fullWidth
                                  label="Color name"
                                  value={cw.name || ""}
                                  onChange={(e) => setFieldValue(`colorways[${i}].name`, e.target.value)}
                                  error={Boolean(touched.colorways?.[i]?.name && errors.colorways?.[i]?.name)}
                                  helperText={touched.colorways?.[i]?.name && errors.colorways?.[i]?.name}
                                />
                              </Grid>

                              <Grid item xs={6}>
                                <input
                                  accept="image/*"
                                  style={{ display: "none" }}
                                  id={`colorways_${i}_image`}
                                  type="file"
                                  onChange={(e) =>
                                    handleIndexedImageChange(e, i, setFieldValue, "colorways", "colorwayPreviews")
                                  }
                                />
                                <label htmlFor={`colorways_${i}_image`}>
                                  <Button component="span">Upload Image</Button>
                                </label>

                                {(previewImages.colorwayPreviews?.[i] || cw.image) && (
                                  <Box mt={1}>
                                    <img
                                      src={previewImages.colorwayPreviews?.[i] || cw.image}
                                      style={{ width: "100%", maxHeight: 180 }}
                                      alt={`Colourway #${i + 1}`}
                                    />
                                    <Stack direction="row" spacing={1} mt={0.5}>
                                      <IconButton
                                        size="small"
                                        onClick={() => {
                                          setFieldValue(`colorways[${i}].image`, null);
                                          setPreviewImages((p) => {
                                            const next = [...(p.colorwayPreviews || [])];
                                            next[i] = null;
                                            return { ...p, colorwayPreviews: next };
                                          });
                                        }}
                                      >
                                        <Delete />
                                      </IconButton>
                                      <Button
                                        size="small"
                                        color="error"
                                        onClick={() => {
                                          arrayHelpers.remove(i);
                                          setPreviewImages((p) => {
                                            const next = [...(p.colorwayPreviews || [])];
                                            next.splice(i, 1);
                                            return { ...p, colorwayPreviews: next };
                                          });
                                        }}
                                      >
                                        Remove row
                                      </Button>
                                    </Stack>
                                  </Box>
                                )}
                              </Grid>
                            </Grid>

                            <Divider sx={{ my: 2 }} />
                          </Box>
                        ))}
                      </>
                    )}
                  />
                </Grid>

                <Divider flexItem sx={{ my: 1, width: "100%" }} />

                {/* Remark */}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Remark"
                    value={values.remark}
                    onChange={(e) => setFieldValue("remark", e.target.value)}
                    multiline
                    rows={3}
                  />
                </Grid>
              </Grid>

              <Box mt={3}>
                <LoadingButton variant="contained" type="submit" loading={isSubmitting}>
                  Save
                </LoadingButton>
              </Box>
            </Form>
          )}
        </Formik>
      </AccordionDetails>
    </Accordion>
  );
};

export default EmbroideryComponent;
